import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { AlertCircle } from 'lucide-react';
import axios from 'axios';
import { getApiErrorMessage } from '../utils/apiError';
import CabinLessonLayout from '../components/lesson/CabinLessonLayout';
import LessonDispatcher from '../components/lesson/LessonDispatcher';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Loading skeleton ────────────────────────────────────────────────────────

function LessonSkeleton() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-app)]" data-testid="lesson-skeleton">
      <div className="w-10 h-10 border-4 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// ─── Error state ─────────────────────────────────────────────────────────────

function LessonError({ onRetry, lang }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[var(--bg-app)]" data-testid="lesson-error">
      <AlertCircle size={40} className="text-rose-400" />
      <p className="font-semibold text-[var(--text-muted)]">
        {lang === 'tr' ? 'Ders yüklenemedi' : 'Could not load lesson'}
      </p>
      {onRetry && (
        <button onClick={onRetry} className="btn-primary px-6 py-2">
          {lang === 'tr' ? 'Tekrar Dene' : 'Retry'}
        </button>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

/**
 * LessonPage — ders verisi ceker ve LessonDispatcher'a iletir.
 * Sayfa, fetch + routing + unmount-time-tracking ile sinirlidir.
 * Ders tipine ozel mantik ilgili sub-bileşende (VideoLesson, PdfLesson, vb.) yasanir.
 */
export default function LessonPage() {
  const { courseId, lessonId } = useParams();
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [lesson, setLesson] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [course, setCourse] = useState(null);
  const [completedLessonIds, setCompletedLessonIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [completing, setCompleting] = useState(false);

  // ── Fetch lesson + course data ──────────────────────────────────────────────
  const fetchData = async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const [lessonRes, courseRes] = await Promise.all([
        axios.get(`${API}/lessons/${lessonId}`),
        axios.get(`${API}/courses/${courseId}`),
      ]);
      setLesson(lessonRes.data);
      // Backend: { course, lessons, reviews } veya eski düz { lessons, ... }
      setCourse(courseRes.data.course || courseRes.data || null);
      setLessons(courseRes.data.lessons || []);

      // Enrollment completedLessons (sidebar icin)
      try {
        const enrRes = await axios.get(`${API}/enrollments`, { headers: authHeaders() });
        const myEnroll = (enrRes.data.enrollments || []).find(e => e.course_id === courseId);
        if (myEnroll) setCompletedLessonIds(myEnroll.completed_lessons || []);
      } catch { /* enrollment optional */ }

      // Derse girildi sinyali (last_lesson_id)
      try {
        await axios.post(
          `${API}/enrollments/${courseId}/mark-visited`,
          { lesson_id: lessonId },
          { headers: authHeaders() }
        );
      } catch { /* mark-visited optional */ }
    } catch (err) {
      console.error('LessonPage fetch error:', err);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const startTime = Date.now();
    const headers = authHeaders();

    fetchData();

    return () => {
      // Unmount: zaman takibini kaydet (visit_only — tamamlama kurali uygulanmaz)
      const minutes = Math.round((Date.now() - startTime) / 60000);
      if (minutes > 0) {
        axios.post(
          `${API}/enrollments/complete-lesson`,
          { course_id: courseId, lesson_id: lessonId, time_spent_minutes: minutes, visit_only: true },
          { headers }
        ).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, lessonId]);

  // ── Completion callback (sub-bileşenlerden çağrılır — navigasyon tetikler) ──
  const handleComplete = (opts) => {
    // completedLessonIds state guncelle (sidebar yansimasi icin)
    setCompletedLessonIds(prev => prev.includes(lessonId) ? prev : [...prev, lessonId]);

    // Sub-bileşen nextLesson navigasyonu isteyebilir
    if (opts?.navigateTo) {
      setTimeout(() => navigate(`/lesson/${courseId}/${opts.navigateTo.lesson_id}`), 800);
      return;
    }

    // Default: sonraki derse git
    const curIdx = lessons.findIndex(l => l.lesson_id === lessonId);
    const next = curIdx >= 0 && curIdx < lessons.length - 1 ? lessons[curIdx + 1] : null;
    if (next) {
      setTimeout(() => navigate(`/lesson/${courseId}/${next.lesson_id}`), 800);
    }
  };

  // ── Manuel "Dersi Tamamla" (navigation bar'dan tetiklenir) ─────────────────
  const handleManualComplete = async () => {
    if (completing) return;
    setCompleting(true);
    try {
      await axios.post(
        `${API}/enrollments/complete-lesson`,
        { course_id: courseId, lesson_id: lessonId },
        { headers: authHeaders() }
      );
      toast.success(lang === 'tr' ? 'Ders tamamlandı!' : 'Lesson completed!');
      handleComplete();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setCompleting(false);
    }
  };

  // ── Derived ────────────────────────────────────────────────────────────────
  const currentLessonIndex = lessons.findIndex(l => l.lesson_id === lessonId);
  const nextLesson = currentLessonIndex >= 0 && currentLessonIndex < lessons.length - 1
    ? lessons[currentLessonIndex + 1]
    : null;
  const prevLesson = currentLessonIndex > 0 ? lessons[currentLessonIndex - 1] : null;

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) return <LessonSkeleton />;
  if (fetchError || !lesson) return <LessonError onRetry={fetchData} lang={lang} />;

  // Tüm kurslar Udemy düzeni (CabinLessonLayout) ile render edilir.
  return (
    <CabinLessonLayout
      lesson={lesson}
      courseId={courseId}
      lessons={lessons}
      completedLessonIds={completedLessonIds}
      course={course}
      prevLesson={prevLesson}
      nextLesson={nextLesson}
      isCompleted={completedLessonIds.includes(lessonId)}
      completing={completing}
      onManualComplete={handleManualComplete}
    >
      {/* Polimorfik ders oynaticisi (VideoLesson, PdfLesson, vb.) */}
      <LessonDispatcher
        lesson={lesson}
        courseId={courseId}
        onComplete={handleComplete}
        completedLessonIds={completedLessonIds}
        lessons={lessons}
        nextLesson={nextLesson}
      />
    </CabinLessonLayout>
  );
}

